/*
    © 2023 EcomGraduates.com
    https://www.ecomgraduates.com
*/

/*
    Get current market exactly like Liquid does
*/
function getCurrentMarket() {
  // Try to get market from Shopify localization if available
  if (window.Shopify?.locale?.country) {
    const country = window.Shopify.locale.country.toLowerCase();
    // Map country codes to markets to match Liquid logic
    switch (country) {
      case "us":
        return "us";
      case "ca":
        return "ca";
      case "au":
        return "australia";
      case "de":
        return "germany";
      case "gb":
        return "uk";
      case "fr":
      case "es":
      case "it":
      case "nl":
      case "be":
      case "at":
      case "se":
      case "dk":
      case "no":
      case "fi":
      case "pl":
      case "pt":
      case "ch":
        return "europe";
      default:
        return "international";
    }
  }

  // Fallback to Shopify.country if available
  if (window.Shopify?.country) {
    const country = window.Shopify.country.toLowerCase();
    switch (country) {
      case "us":
      case "united states":
        return "us";
      case "ca":
      case "canada":
        return "ca";
      case "au":
      case "australia":
        return "australia";
      case "de":
      case "germany":
        return "germany";
      case "gb":
      case "uk":
      case "united kingdom":
        return "uk";
      case "fr":
      case "es":
      case "it":
      case "nl":
      case "be":
      case "at":
      case "se":
      case "dk":
      case "no":
      case "fi":
      case "pl":
      case "pt":
      case "ch":
        return "europe";
      default:
        return "international";
    }
  }

  // Default fallback
  return "us";
}

/*
    Get market-specific fake discount settings exactly like Liquid
    Uses the pre-computed marketSettings from theme.liquid which already
    contains the correct settings for the current market.
*/
function getMarketSettings(market) {
  const fakeDiscount = window.theme?.fakeDiscount || {};

  // Use the pre-computed marketSettings from theme.liquid
  // Liquid already determines the correct market and exposes the right settings
  const marketSettings = fakeDiscount.marketSettings || {};

  return {
    enabled: marketSettings.enabled || false,
    type: marketSettings.type || "percentage",
    percentage: marketSettings.percentage || 20,
    fixedAmount: marketSettings.fixedAmount || "10.00",
    tag: marketSettings.tag || "fake-markdown",
    code: marketSettings.code || "SAVE20",
    message: marketSettings.message || "<p><strong>[percentage]% off</strong> when using code <strong>[code]</strong></p>",
    messageColor: marketSettings.messageColor || "#22c55e",
    market: marketSettings.market || market,
  };
}

/*
    Check if product has fake discount tag
*/
function productHasFakeDiscountTag(productTags, requiredTag) {
  if (!productTags || !requiredTag) return false;

  // Handle both string and array formats for tags
  const tags = Array.isArray(productTags) ? productTags : (productTags || "").split(",").map((tag) => tag.trim());

  return tags.includes(requiredTag);
}

/*
    Calculate fake discount exactly like Liquid does
*/
function calculateFakeDiscount(product, variant, displayType = "price") {
  const currentMarket = getCurrentMarket();
  const marketSettings = getMarketSettings(currentMarket);

  // Check if fake discount should be applied
  const productTags = product?.tags || window.productTags || [];
  const shouldApplyDiscount = marketSettings.enabled && productHasFakeDiscountTag(productTags, marketSettings.tag);

  if (!shouldApplyDiscount) {
    return {
      shouldApply: false,
      originalPrice: null,
      discountedPrice: null,
      discountPercentage: 0,
      message: null,
      marketSettings: marketSettings,
    };
  }

  // Get base price based on display type and price source setting
  let basePrice;
  const priceSource = window.theme?.fakeDiscount?.priceSource || "price";

  if (displayType === "price-item") {
    // For product items, use product-level pricing
    if (priceSource === "price") {
      basePrice = product.price;
    } else {
      // Use compare_at_price if available, otherwise fallback to price
      if (product.compare_at_price && product.compare_at_price > product.price) {
        basePrice = product.compare_at_price;
      } else {
        basePrice = product.price;
      }
    }
  } else {
    // For individual products, use variant pricing
    const currentVariant = variant || (product.variants && product.variants[0]) || {};
    if (priceSource === "price") {
      basePrice = currentVariant.price;
    } else {
      // Use compare_at_price if available, otherwise fallback to price
      if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
        basePrice = currentVariant.compare_at_price;
      } else {
        basePrice = currentVariant.price;
      }
    }
  }

  if (!basePrice) {
    return {
      shouldApply: false,
      originalPrice: null,
      discountedPrice: null,
      discountPercentage: 0,
      message: null,
      marketSettings: marketSettings,
    };
  }

  // Calculate discount based on type
  let discountedPrice;
  let calculatedPercentage;

  if (marketSettings.type === "fixed") {
    // Fixed amount discount (subtract this amount from the price)
    const discountAmount = parseFloat(marketSettings.fixedAmount) * 100;
    discountedPrice = basePrice - discountAmount;
    // Ensure we have at least 1 cent
    if (discountedPrice < 1) {
      discountedPrice = 1;
    }
    calculatedPercentage = Math.round((discountAmount / basePrice) * 100);
  } else {
    // Percentage discount (default)
    // Follow exact Liquid logic: discount_amount = base_price | times: market_percentage | divided_by: 100
    const discountAmount = Math.round((basePrice * marketSettings.percentage) / 100);
    discountedPrice = basePrice - discountAmount;
    calculatedPercentage = marketSettings.percentage;
    
    // Round up to nearest 10 cents for percentage discounts only (e.g., $7.19 → $7.20)
    // Prices are in cents, so round to nearest 10
    discountedPrice = Math.ceil(discountedPrice / 10) * 10;
  }

  // Generate promotional message
  let message = marketSettings.message;
  message = message.replace(/\[percentage\]/g, marketSettings.percentage);
  message = message.replace(/\[amount\]/g, marketSettings.fixedAmount);
  message = message.replace(/\[code\]/g, marketSettings.code);
  message = message.replace(/\[percentage-amount\]/g, calculatedPercentage);

  return {
    shouldApply: true,
    originalPrice: basePrice,
    discountedPrice: discountedPrice,
    discountPercentage: calculatedPercentage,
    message: message,
    marketSettings: marketSettings,
  };
}

// /*
//     Create background color for fake discount message
// */
// function getFakeDiscountMessageBgColor(messageColor) {
//   switch (messageColor) {
//     case "#22c55e":
//       return "#f0f8f0";
//     case "#ff0000":
//       return "#fff0f0";
//     case "#0000ff":
//       return "#f0f0ff";
//     case "#ff6600":
//       return "#fff6f0";
//     case "#9900cc":
//       return "#f9f0fc";
//     default:
//       return "#f8f9fa";
//   }
// }

/*
    Get fake discount percentage (legacy function for backward compatibility)
*/
function getFakeDiscountPercentage() {
  const currentMarket = getCurrentMarket();
  const marketSettings = getMarketSettings(currentMarket);
  return marketSettings.percentage || 20;
}

/*
    Main 'Add to cart' (ATC) form
*/
window.onSubmitAtcForm = async (form, event) => {
  event.preventDefault();

  const btn = form.querySelector(".btn-atc");
  btn.innerHTML = `
        <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    `;
  form.classList.add("loading");

  const response = await fetch("/cart/add.js", {
    method: "POST",
    body: new FormData(form),
  });

  form.classList.remove("loading");
  btn.innerHTML = "Added to Cart";

  setTimeout(() => {
    btn.innerHTML = btn.dataset.textAddToCart;
  }, 2000);

  window.refreshCartContents(response);

  if (form.hasAttribute("data-ecomify-upsell-modal")) return;
  if (btn.closest(".modal")) return;

  if (document.querySelector("#offcanvas-cart")) {
    bootstrap.Offcanvas.getOrCreateInstance("#offcanvas-cart").show();
  }

  const _learnq = window._learnq || [];
  _learnq.push([
    "track",
    "Added to Cart",
    {
      ProductID: form.dataset.productId,
      Name: form.dataset.productTitle,
      URL: form.dataset.productUrl,
    },
  ]);

  // Spotify Pixel - Add to Cart Event
  if (window.spdt && form.dataset.productId) {
    const variantId = form.querySelector('input[name="id"]')?.value;
    const quantity = form.querySelector('input[name="quantity"]')?.value || 1;
    const productTitle = form.dataset.productTitle || "Product";

    // Get product price from multiple sources
    let productPrice = 0;

    // Try to get price from variant data first
    if (variantId && window.Shopify?.meta?.product?.variants) {
      const variant = window.Shopify.meta.product.variants.find((v) => v.id == variantId);
      if (variant) {
        productPrice = variant.price / 100; // Convert from cents
      }
    }

    // Fallback: try to get price from button text
    if (productPrice === 0) {
      const priceElement = form.querySelector(".btn-atc-price");
      if (priceElement) {
        const priceText = priceElement.textContent.replace(/[^\d.,]/g, "");
        productPrice = parseFloat(priceText.replace(",", ".")) || 0;
      }
    }

    // Fallback: try to get price from product data
    if (productPrice === 0 && window.Shopify?.meta?.product?.price) {
      productPrice = window.Shopify.meta.product.price / 100;
    }

    // Fallback: try to get price from current variant
    if (productPrice === 0 && window.Shopify?.meta?.product?.selected_or_first_available_variant?.price) {
      productPrice = window.Shopify.meta.product.selected_or_first_available_variant.price / 100;
    }

    // Fallback: try to get price from page data attributes
    if (productPrice === 0) {
      const priceAttr = document.querySelector("[data-cnstrc-item-price]")?.getAttribute("data-cnstrc-item-price");
      if (priceAttr) {
        productPrice = parseFloat(priceAttr);
      }
    }

    const eventData = {
      value: productPrice,
      currency: window.Shopify?.currency?.active || "USD",
      quantity: parseInt(quantity),
      event_id: "add_to_cart_" + form.dataset.productId + "_" + Date.now(),
      product_id: form.dataset.productId,
      product_name: productTitle,
      product_type: form.dataset.productType || null,
      product_vendor: form.dataset.productVendor || null,
    };

    try {
      window.spdt("addtocart", eventData);
    } catch (error) {
      console.error("Spotify Pixel error:", error);
    }
  }
};

/*
    'Add to cart' form based on variant
*/
window.onClickAtcFormVariant = async (btn, event) => {
  const form = btn.closest("form");
  form.querySelector('[name="id"]').value = btn.dataset.variantId;

  window.onSubmitAtcForm(form, event);
};

document.addEventListener("DOMContentLoaded", function () {
  const form = document.querySelector("form");
  if (form && form.dataset.variants) {
    const variants = JSON.parse(form.dataset.variants);
    const variantId = form.querySelector('[name="id"]').value;
    const selectedVariant = variants.find((variant) => variant.id == variantId);
    if (selectedVariant) {
      const selectedOptions = selectedVariant.options;
      updateSwatchesState(variants, selectedOptions);
      renderSizeButtons(variants, selectedOptions, form);
    }
  }
});

function updateSwatchesState(variants, selectedOptions) {
  const groups = document.querySelectorAll(".product-option-wrapper");
  const validCombinations = variants.filter((variant) => variant.available);

  groups.forEach((group) => {
    const swatches = group.querySelectorAll(".color-swatches li");

    swatches.forEach((swatch) => {
      const value = swatch.dataset.value;
      const optionPosition = parseInt(swatch.dataset.optionPosition) - 1;

      const isAvailable = variants.some((variant) => {
        const match = variant.options[optionPosition] === value;
        return (
          match &&
          variant.available &&
          selectedOptions.every((opt, idx) => (idx === optionPosition ? true : variant.options[idx] === opt))
        );
      });
    });
  });
  manageSwatchesAvailability(selectedOptions, validCombinations);
}

document.querySelectorAll(".product-option").forEach((input) => {
  input.addEventListener("change", () => window.onChangeProductOption(input));
});

// Rebuild the Size buttons (option2) on the fly based on current Color (option1)
function renderSizeButtons(variants, selectedOptions, form) {
  const sizeList = document.querySelector(".size-buttons");
  if (!sizeList) return;

  // Support 2 or 3 option products; infer which option index is the Size group
  const optionsLen = Array.isArray(variants?.[0]?.options) ? variants[0].options.length : 0;
  if (!optionsLen) return;

  const productId = form?.dataset?.productId || "product";

  // Determine the input name/id prefix from any existing size input (fallback to a default)
  const existingInput = sizeList.querySelector("input.product-option");
  const nameAttr = existingInput?.getAttribute("name") || `${productId}-option-size`;
  const optionHandle = nameAttr.split("-option-")[1] || "size";

  // Infer size index by comparing existing labels with option sets; fallback to last index
  const existingLabels = Array.from(sizeList.querySelectorAll("label"))
    .map((l) => l.textContent.trim())
    .filter(Boolean);
  const optionSets = Array.from({ length: optionsLen }, (_, idx) => new Set(variants.map((v) => v.options[idx])));
  let sizeIndex = optionsLen - 1;
  if (existingLabels.length) {
    for (let i = 0; i < optionsLen; i++) {
      const s = optionSets[i];
      if (existingLabels.every((val) => s.has(val))) {
        sizeIndex = i;
        break;
      }
    }
  }

  // All distinct size values for inferred index
  const allSizes = Array.from(new Set(variants.map((v) => v.options[sizeIndex])));

  // Get current market for market-specific availability check
  const market = (function () {
    try {
      return (
        (typeof getCurrentMarket !== "undefined" && getCurrentMarket && getCurrentMarket()) ||
        document.querySelector(".btn-atc")?.dataset?.currentMarket ||
        "us"
      );
    } catch (e) {
      return "us";
    }
  })();

  // Get market-specific availability data
  let availabilityMap = {};
  try {
    const availabilityEl = document.querySelector("[data-variant-availability]");
    if (availabilityEl) {
      availabilityMap = JSON.parse(availabilityEl.textContent || "{}");
    }
  } catch (e) {
    availabilityMap = {};
  }

  // Helper function to check market-specific availability
  const isMarketAvailable = (variant) => {
    const variantAvailData = availabilityMap[String(variant.id)] || {};
    let isAvail = variant.available; // Default to Shopify's native availability
    let isPreorder = false;

    switch (market) {
      case "us":
        isAvail = variantAvailData.available_us !== false;
        isPreorder = !!variantAvailData.preorderus || !!variantAvailData.preorder;
        break;
      case "uk":
        isAvail = variantAvailData.available_uk !== false;
        isPreorder = !!variantAvailData.preorderuk || !!variantAvailData.preorder;
        break;
      case "europe":
        isAvail = variantAvailData.available_europe !== false;
        isPreorder = !!variantAvailData.preordereurope || !!variantAvailData.preorder;
        break;
      case "australia":
        isAvail = variantAvailData.available_australia !== false;
        isPreorder = !!variantAvailData.preorderaustralia || !!variantAvailData.preorder;
        break;
      case "ca":
        isAvail = variantAvailData.available_ca !== false;
        isPreorder = !!variantAvailData.preorder;
        break;
      case "germany":
        isAvail = variantAvailData.available_europe !== false;
        isPreorder = !!variantAvailData.preordergermany || !!variantAvailData.preorder;
        break;
      default:
        isAvail = variant.available;
        isPreorder = !!variantAvailData.preorder;
    }

    return isPreorder || isAvail;
  };

  const handleize = (val) =>
    (val || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  let html = "";
  allSizes.forEach((sizeValue) => {
    const isAvailable = variants.some((v) => {
      if (v.options[sizeIndex] !== sizeValue) return false;
      // Check market-specific availability instead of just v.available
      if (!isMarketAvailable(v)) return false;
      // Ensure other selected options (color/stone/etc) match if selected
      return selectedOptions.every((opt, idx) => (idx === sizeIndex ? true : opt == null || v.options[idx] === opt));
    });

    const isChecked = selectedOptions?.[sizeIndex] === sizeValue;
    const id = `${productId}-option-${optionHandle}-${handleize(sizeValue)}`;

    if (isAvailable) {
      html += `
        <li class="p-2">
          <input
            class="product-option visually-hidden"
            type="radio"
            name="${nameAttr}"
            id="${id}"
            value="${sizeValue.replace(/\"/g, "&quot;")}"
            data-product-id="${productId}"
            ${isChecked ? "checked" : ""}
            onchange="onChangeProductOption(this, event)"
          >
          <label for="${id}" class="rounded">${sizeValue}</label>
        </li>`;
    }
  });

  sizeList.innerHTML = html;

  // If the previously selected size is no longer available, auto-select the first available and trigger change
  const hasChecked = !!sizeList.querySelector("input:checked");
  if (!hasChecked) {
    const firstAvailable = sizeList.querySelector("input");
    if (firstAvailable) {
      firstAvailable.checked = true;
      // Recurse into the normal handler to update everything else
      window.onChangeProductOption(firstAvailable);
    }
  }
}

function manageSwatchesAvailability(selectedOptions, validCombinations) {
  const firstGroupOptions = document.querySelectorAll('.product-option-wrapper[data-group="1"] input');
  const secondGroupOptions = document.querySelectorAll('.product-option-wrapper[data-group="2"] input');

  firstGroupOptions.forEach((input) => {
    const selectedValue = input.checked ? input.value : null;

    secondGroupOptions.forEach((secondInput) => {
      const secondValue = secondInput.value;

      const isAvailable = validCombinations.some(
        (variant) => variant.available && variant.options[0] === selectedValue && variant.options[1] === secondValue
      );

      if (isAvailable) {
        secondInput.disabled = false;
        secondInput.closest("li").classList.remove("sold-out-variant", "disabled");
      } else {
        secondInput.disabled = true;
        secondInput.closest("li").classList.add("sold-out-variant", "disabled");
      }
    });
  });

  firstGroupOptions.forEach((input) => {
    input.addEventListener("change", () => {
      const selectedValue = input.value;

      secondGroupOptions.forEach((secondInput) => {
        const secondValue = secondInput.value;

        const isAvailable = validCombinations.some(
          (variant) => variant.available && variant.options[0] === selectedValue && variant.options[1] === secondValue
        );

        if (isAvailable) {
          secondInput.disabled = false;
          secondInput.closest("li").classList.remove("sold-out-variant", "disabled");
        } else {
          secondInput.disabled = true;
          secondInput.closest("li").classList.add("sold-out-variant", "disabled");
        }
      });
    });
  });
}

/*
    Product options selector - Listen for change events
*/
window.onChangeProductOption = async (input) => {
  const selectedOptions = [];
  const form = input.closest("form");
  const moneyWithCurrencyFormat =
    Shopify.money_with_currency_format ||
    (Shopify.money_format
      ? `${Shopify.money_format} ${Shopify.currency?.active || Shopify.shop_currency || ""}`
      : Shopify.money_format);

  input
    .closest("form")
    .querySelectorAll(".product-option")
    .forEach((elem) => {
      if (elem.type === "radio") {
        if (elem.checked) {
          selectedOptions.push(elem.value);
        }
      } else {
        selectedOptions.push(elem.value);
      }
    });

  const variants = JSON.parse(form.dataset.variants);
  // Always rebuild size buttons based on the currently selected non-size options
  renderSizeButtons(variants, selectedOptions, form);

  const selectedVariant = variants.find((variant) => JSON.stringify(variant.options) === JSON.stringify(selectedOptions));

  const btn = form.querySelector(".btn-atc");

  if (selectedVariant) {
    form.querySelector('[name="id"]').value = selectedVariant.id;

    // Get market-specific availability from metafields
    const market = (function () {
      try {
        return (
          (typeof getCurrentMarket !== "undefined" && getCurrentMarket && getCurrentMarket()) ||
          document.querySelector(".btn-atc")?.dataset?.currentMarket ||
          "us"
        );
      } catch (e) {
        return "us";
      }
    })();

    // Check market-specific availability metafields
    let isMarketAvailable = selectedVariant.available; // Default to Shopify's native availability
    let isPreorder = false;

    try {
      // Try to get availability from our data-variant-availability JSON
      const availabilityEl = document.querySelector("[data-variant-availability]");
      if (availabilityEl) {
        const availabilityMap = JSON.parse(availabilityEl.textContent || "{}");
        const variantAvailData = availabilityMap[String(selectedVariant.id)] || {};

        // Helper: if metafield is null (empty), fall back to Shopify stock
        const getMarketAvailable = (metafieldValue) => {
          if (metafieldValue === null || metafieldValue === undefined) {
            return variantAvailData.shopify_available ?? selectedVariant.available;
          }
          return metafieldValue !== false;
        };

        switch (market) {
          case "us":
            isMarketAvailable = getMarketAvailable(variantAvailData.available_us);
            isPreorder = !!variantAvailData.preorderus || !!variantAvailData.preorder;
            break;
          case "uk":
            isMarketAvailable = getMarketAvailable(variantAvailData.available_uk);
            isPreorder = !!variantAvailData.preorderuk || !!variantAvailData.preorder;
            break;
          case "europe":
            isMarketAvailable = getMarketAvailable(variantAvailData.available_europe);
            isPreorder = !!variantAvailData.preordereurope || !!variantAvailData.preorder;
            break;
          case "australia":
            isMarketAvailable = getMarketAvailable(variantAvailData.available_australia);
            isPreorder = !!variantAvailData.preorderaustralia || !!variantAvailData.preorder;
            break;
          case "ca":
            isMarketAvailable = getMarketAvailable(variantAvailData.available_ca);
            isPreorder = !!variantAvailData.preorder;
            break;
          case "germany":
            isMarketAvailable = getMarketAvailable(variantAvailData.available_europe);
            isPreorder = !!variantAvailData.preordergermany || !!variantAvailData.preorder;
            break;
          case "international":
            isMarketAvailable = variantAvailData.shopify_available ?? selectedVariant.available;
            isPreorder = !!variantAvailData.preorder;
            break;
          default:
            isMarketAvailable = selectedVariant.available;
            isPreorder = !!variantAvailData.preorder;
        }
      }

    } catch (_) {
      // Fallback to native availability
      isMarketAvailable = selectedVariant.available;
      isPreorder = false;
    }

    // Use market availability OR preorder to determine if button should be enabled
    if (isPreorder || isMarketAvailable) {
      btn.disabled = false;

      if (isPreorder) {
        btn.querySelector(".btn-atc-text").textContent = btn.dataset.textPreorder || "Pre-order";
      } else {
        btn.querySelector(".btn-atc-text").textContent = btn.dataset.textAddToCart || "Add To Cart";
      }
    } else {
      btn.disabled = true;
      btn.querySelector(".btn-atc-text").textContent = btn.dataset.textSoldOut || "Sold Out";
    }

    const preorderPropertyField = form.querySelector('#preorder-property-field');
    if (preorderPropertyField) {
      preorderPropertyField.disabled = !isPreorder;
    }

    const customEvent = new CustomEvent("variantChange.ecomify.product", {
      detail: selectedVariant,
    });
    window.dispatchEvent(customEvent);

    updateSwatchesState(variants, selectedOptions);

    if (selectedVariant.featured_image) {
      const productItem = form.closest(".product-item");
      if (productItem) {
        const productImage = productItem.querySelector(".product-item-img");
        if (productImage) {
          productImage.src = selectedVariant.featured_image.src;
        }
      }
    }

    const fakeDiscountData = calculateFakeDiscount(
      {
        tags: window.productTags,
        price: selectedVariant.price,
        compare_at_price: selectedVariant.compare_at_price,
      },
      selectedVariant,
      "price"
    );

    let finalPrice = selectedVariant.price;
    let finalCompareAtPrice = selectedVariant.compare_at_price;

    if (fakeDiscountData.shouldApply) {
      finalPrice = fakeDiscountData.discountedPrice;
      finalCompareAtPrice = selectedVariant.compare_at_price;
    }

    const priceContainer = form?.querySelector(".product-price");

    if (priceContainer) {
      // Save any existing elements we want to preserve (tooltip, badges, etc.)
      const elementsToPreserve = Array.from(priceContainer.children).filter(el => 
        !el.classList.contains('product-price-compare') && 
        !el.classList.contains('product-price-final')
      );

      // Clear only the price elements
      priceContainer.querySelectorAll('.product-price-compare, .product-price-final').forEach(el => el.remove());

      // Build new price HTML
      let priceHTML = '';
      
      if (finalCompareAtPrice > finalPrice) {
        // Product is on sale - show compare price
        priceHTML = `
          <span class="product-price-compare text-muted me-3">
            <span class="visually-hidden">
              ${window.theme.product.priceFrom} &nbsp;
            </span>
            <s>${Shopify.formatMoney(finalCompareAtPrice, moneyWithCurrencyFormat)}</s>
          </span>
          <span class="product-price-final">
            <span class="visually-hidden">
              ${window.theme.product.priceSale} &nbsp;
            </span>
            ${Shopify.formatMoney(finalPrice, moneyWithCurrencyFormat)}
          </span>
        `;
      } else {
        // Product is regular price
        priceHTML = `
          <span class="product-price-final">
            ${Shopify.formatMoney(finalPrice, moneyWithCurrencyFormat)}
          </span>
        `;
      }

      // Insert price HTML at the beginning
      priceContainer.insertAdjacentHTML('afterbegin', priceHTML);

      // Re-append preserved elements after the price
      elementsToPreserve.forEach(el => priceContainer.appendChild(el));
    }
    // if (selectedVariant.available && finalCompareAtPrice > finalPrice && priceContainer) {
    //   // priceContainer.insertAdjacentHTML(
    //   //   "beforeend",
    //   //   `
    //   //           <span class="price-badge-sale badge">
    //   //               ${window.theme.product.save}: ${Shopify.formatMoney(finalCompareAtPrice - finalPrice)}
    //   //           </span>
    //   //       `
    //   // );
    // } else if (!selectedVariant.available && priceContainer) {
    //   priceContainer.insertAdjacentHTML(
    //     "beforeend",
    //     `
    //             <span class="price-badge-sold-out badge">
    //                 Sold Out
    //             </span>
    //         `
    //   );
    // }

    const purchaseOptionsWrapper = document.querySelector("#product-purchase-options");

    if (purchaseOptionsWrapper) {
      purchaseOptionsWrapper.querySelector("input#purchase-options-one-time + label [data-price]").textContent =
        Shopify.formatMoney(finalPrice);
      purchaseOptionsWrapper.querySelector("input#purchase-options-subscription + label [data-price]").textContent =
        Shopify.formatMoney(selectedVariant.selling_plan_allocations[0].price);
    }

    const productContent =
      input.closest(".product-content") || form?.closest(".product-content") || document.querySelector(".product-content");
    const skuWrapper = productContent?.querySelector(".current-variant-sku");

    if (skuWrapper) {
      if (selectedVariant.sku.length) {
        skuWrapper.textContent = selectedVariant.sku;
        skuWrapper.removeAttribute("hidden");
      } else {
        skuWrapper.setAttribute("hidden", "hidden");
      }
    }

    if (input.closest("#product-template")) {
      const url = new URL(window.location);
      url.searchParams.set("variant", selectedVariant.id);
      window.history.replaceState({}, "", url);
    }

    if (btn.querySelector(".btn-atc-price")) {
      btn.querySelector(".btn-atc-price").textContent = Shopify.formatMoney(finalPrice, moneyWithCurrencyFormat);
    }

    const fakeDiscountMessageEl = input.closest(".product-content").querySelector(".fake-discount-message");
    if (fakeDiscountMessageEl && fakeDiscountData.shouldApply) {
      fakeDiscountMessageEl.innerHTML = fakeDiscountData.message;
      fakeDiscountMessageEl.style.color = fakeDiscountData.marketSettings.messageColor;
      fakeDiscountMessageEl.style.backgroundColor = getFakeDiscountMessageBgColor(fakeDiscountData.marketSettings.messageColor);
    }
  } else {
    btn.disabled = true;
    btn.querySelector(".btn-atc-text").textContent = "Unavailable";
  }

  if (input.closest(".color-swatches")) {
    let selectedColor = input.value;
    input.closest(".product-option-wrapper").querySelector(".color-swatches-title span").textContent = selectedColor;
  }

  // Update preview when font changes
  if (input.closest(".product-option.form-select") && window.updateLabelPreview) {
    setTimeout(() => {
      window.updateLabelPreview();
    }, 10);
  }

  if (input.closest(".size-buttons")) {
    input.closest(".product-option-wrapper").querySelector(".size-buttons-title span").textContent = input.value;
  }
};

/*
    Update various elements on variant change that need a new document fetch
*/
window.addEventListener("variantChange.ecomify.product", async (event) => {
  const inventoryBar = document.querySelector("#inventory-bar");

  if (inventoryBar) {
    inventoryBar.style.opacity = ".25";
  }

  const respoonse = await fetch(window.location.href);
  const text = await respoonse.text();
  const newDocument = new DOMParser().parseFromString(text, "text/html");

  document.querySelector("#inventory-bar")?.replaceWith(newDocument.querySelector("#inventory-bar"));
  document.querySelector("#product-qty-breaks")?.replaceWith(newDocument.querySelector("#product-qty-breaks"));

  document
    .querySelector("#product-store-availability-wrapper")
    ?.replaceWith(newDocument.querySelector("#product-store-availability-wrapper"));

  initInventoryBar();
  initQtyBreaks();

  if (document.querySelector(".product-gallery-variant-based")) {
    document
      .querySelector(".product-gallery-variant-based")
      ?.replaceWith(newDocument.querySelector(".product-gallery-variant-based"));
    initProductGallery();
  } else {
    const selectedVariant = event.detail;
    if (selectedVariant && selectedVariant.featured_image) {
      const mainImage = document.querySelector(".product-gallery .main-splide img");
      if (mainImage) {
        mainImage.src = selectedVariant.featured_image.src;
      }
    }
  }

  if (document.querySelector("#product-block-variant-description")) {
    document
      .querySelector("#product-block-variant-description")
      ?.replaceWith(newDocument.querySelector("#product-block-variant-description"));
  }

  document.querySelectorAll('[role="tooltip"]').forEach((el) => el.remove());

  document.querySelectorAll('.product-options [data-bs-toggle="tooltip"]').forEach((el) => new bootstrap.Tooltip(el));
});

/*
    Purchase options (selling/subscription plans)
*/
const initPurchaseOptions = () => {
  const wrapper = document.querySelector("#product-purchase-options");

  if (!wrapper) return;

  wrapper.querySelectorAll('[name="purchase_option"]').forEach((input) => {
    input.addEventListener("change", () => {
      if (input.id === "purchase-options-one-time") {
        wrapper.querySelector("#purchase-delivery-options").setAttribute("hidden", "hidden");
        wrapper.querySelector('[name="selling_plan"]').disabled = true;

        if (input.closest("#product-template")) {
          const url = new URL(window.location);
          url.searchParams.delete("selling_plan");
          window.history.replaceState({}, "", url);
        }
        const btnAtcPrice = input.closest("form").querySelector(".btn-atc-price");

        if (btnAtcPrice) {
          btnAtcPrice.textContent = Shopify.formatMoney(input.dataset.price);
        }
      } else {
        wrapper.querySelector("#purchase-delivery-options").removeAttribute("hidden");
        wrapper.querySelector('[name="selling_plan"]').disabled = false;

        if (input.closest("#product-template")) {
          const url = new URL(window.location);
          url.searchParams.set("variant", wrapper.closest(".product-content").querySelector('[name="id"]').value);
          url.searchParams.set("selling_plan", wrapper.querySelector('[name="selling_plan"]').value);
          window.history.replaceState({}, "", url);
        }

        const btnAtcPrice = input.closest("form").querySelector(".btn-atc-price");

        if (btnAtcPrice) {
          btnAtcPrice.textContent = Shopify.formatMoney(input.dataset.price);
        }
      }
    });
  });

  const deliverEverySelect = wrapper.querySelector("#purchase-delivery-options-select");

  if (deliverEverySelect) {
    deliverEverySelect.addEventListener("change", () => {
      if (deliverEverySelect.closest("#product-template")) {
        const url = new URL(window.location);
        url.searchParams.set("selling_plan", wrapper.querySelector('[name="selling_plan"]').value);
        window.history.replaceState({}, "", url);
      }
    });
  }

  if (wrapper.dataset.subSelectedFirst === "true") {
    wrapper.querySelector("input#purchase-options-subscription").checked = true;
    wrapper.querySelector("input#purchase-options-subscription").dispatchEvent(new Event("change"));
  }
};
initPurchaseOptions();

// Product Item - Handle variant change
window.onChangeProductItemVariant = (select, event) => {
  if (select.options[select.selectedIndex].dataset.variantImage?.length) {
    select.closest(".product-item").querySelector(".product-item-img").src =
      select.options[select.selectedIndex].dataset.variantImage;
  }

  const compareAtPrice = select.options[select.selectedIndex].dataset.compareAtPrice;
  const price = select.options[select.selectedIndex].dataset.price;

  const productItem = select.closest(".product-item");
  const productTags = productItem.dataset.productTags ? productItem.dataset.productTags.split(",") : [];

  const fakeDiscountData = calculateFakeDiscount(
    {
      tags: productTags,
      price: parseInt(price),
      compare_at_price: parseInt(compareAtPrice),
    },
    { price: parseInt(price), compare_at_price: parseInt(compareAtPrice) },
    "price-item"
  );

  let finalPrice = price;
  let finalCompareAtPrice = compareAtPrice;

  if (fakeDiscountData.shouldApply) {
    finalPrice = fakeDiscountData.discountedPrice;
    finalCompareAtPrice = compareAtPrice;
  }

  if (finalCompareAtPrice.length && finalCompareAtPrice != finalPrice) {
    select.closest(".product-item").querySelector(".product-item-price").innerHTML = `
            <span class="product-item-price-compare text-muted me-1">
                <span class="visually-hidden">
                    ${window.theme.product.priceFrom} &nbsp;
                </span>
                 
                    ${Shopify.formatMoney(finalCompareAtPrice)}
                
            </span>
            <span class="product-item-price-final">
                <span class="visually-hidden">
                    ${window.theme.product.priceSale} &nbsp;
                </span>
                ${Shopify.formatMoney(finalPrice)}
            </span>
        `;
  } else {
    select.closest(".product-item").querySelector(".product-item-price").innerHTML = `
            <span class="product-item-price-final">
                ${Shopify.formatMoney(finalPrice)}
            </span>
        `;
  }
};

// Product Item - Handle swatches change
window.onChangeProductItemSwatches = (btn, event) => {
  const productItem = btn.closest(".product-item");

  if (productItem.querySelector(".product-carousel")) {
    productItem.querySelector(".product-carousel .carousel-inner").insertAdjacentHTML(
      "beforeend",
      `
            <div class="carousel-item">
                <img
                    class="product-item-img img-fluid rounded ${btn.dataset.imgThumbnail}"
                    src="${btn.dataset.img}"
                    alt=""
                    width="${btn.dataset.imgWidth}"
                    height="${btn.dataset.imgHeight}"
                    loading="lazy">
            </div>
        `
    );
    const index = productItem.querySelectorAll(".product-carousel .carousel-item").length - 1;
    bootstrap.Carousel.getOrCreateInstance(productItem.querySelector(".product-carousel")).to(index);
  } else {
    productItem.querySelector(".product-item-img").setAttribute("src", btn.dataset.img);
  }
};

/*
    'Buy it now' button
*/
/*
    'Buy it now' button with verification modal
*/
window.onClickBuyBtn = (btn) => {
  const form = btn.closest("form");
  const variantId = form.querySelector('[name="id"]').value;
  const qty = Number(form.querySelector('input[name="quantity"]')?.value || 1);
  const cartUrl = `/cart/${variantId}:${qty}`;

  // Verificação da tag "backing"
  const hasBackingTag = window.productTags && window.productTags.includes("backing");

  if (hasBackingTag && !window.allowFormSubmit) {
    showBackingModalCheckout(cartUrl);
    return;
  }

  btn.innerHTML = `
    <div class="spinner-border spinner-border-sm" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  `;
  window.location.href = cartUrl;
};

function showBackingModalCheckout(cartUrl) {
  if (!document.getElementById("backingModalCheckout")) {
    document.body.insertAdjacentHTML(
      "beforeend",
      `
      <div id="backingModalCheckout" class="backing-modal" style="display:none;">
          <div class="modal-backing-content">
              <span class="modal-close">&times;</span>
              <p>This phone cover can only be used with our Universal Phone Base.</p>
              <p>
                  Please consider adding the
                  <a href="https://evryjewels.com/products/universal-phone-case">Universal Phone Base</a> to your cart.
              </p>
              <div class="modal-buttons">
                  <button id="modalProceedToCheckout" class="btn-atc btn btn-primary w-100">Proceed to Checkout</button>
                  <button id="modalCancelCheckout" class="btn-buy btn btn-sm btn-outline-secondary w-100 mt-2">Cancel</button>
              </div>
          </div>
      </div>
    `
    );
  }

  const modal = document.getElementById("backingModalCheckout");
  modal.style.display = "block";

  // Eventos para fechar o modal e confirmar o redirecionamento
  modal.querySelector(".modal-close").onclick = () => (modal.style.display = "none");
  document.getElementById("modalCancelCheckout").onclick = () => (modal.style.display = "none");

  document.getElementById("modalProceedToCheckout").onclick = function () {
    modal.style.display = "none";
    window.allowFormSubmit = true;
    window.location.href = cartUrl;
  };

  window.onclick = function (event) {
    if (event.target === modal) {
      modal.style.display = "none";
    }
  };
}

/*
    Product Gallery
*/
const initProductGallery = () => {
  document.querySelectorAll(".product-gallery:not(.init)").forEach((gallery) => {
    gallery.classList.add("init");

    const totalSlides = gallery.querySelectorAll(".main-splide .splide__slide").length;

    const mainSplideElement = gallery.querySelector(".main-splide");

    const main = new Splide(mainSplideElement, {
      start: Number(gallery.dataset.start),
      rewind: totalSlides > 1,
      arrows: Number(gallery.dataset.mediaCount > 1) && gallery.dataset.arrows === "main-slider" && totalSlides > 1,
      rewindByDrag: totalSlides > 1,
      drag: totalSlides > 1,
      pagination: false,
      noDrag: "model-viewer",
      mediaQuery: "min",
      breakpoints: {
        0: {
          padding: {
            right: gallery.dataset.showThumbsMobile === "false" ? "4rem" : 0,
          },
        },
        992: { padding: 0 },
      },
      direction: document.documentElement.getAttribute("dir"),
    });

    if (totalSlides === 1) {
      mainSplideElement.querySelector(".splide__track").style.overflow = "hidden";
    }

    const thumbs = new Splide(gallery.querySelector(".thumbs-splide"), {
      start: Number(gallery.dataset.start),
      arrows: Number(gallery.dataset.mediaCount > 3) && gallery.dataset.arrows === "thumbs-slider" && totalSlides > 1,
      gap: ".5rem",
      rewind: totalSlides > 1,
      rewindByDrag: totalSlides > 1,
      pagination: false,
      isNavigation: totalSlides > 1,
      focus: totalSlides > 5 ? "center" : 0,
      mediaQuery: "min",
      breakpoints: {
        0: { perPage: 5 },
        576: { perPage: 5 },
        768: { perPage: 5 },
        992: { perPage: 5 },
        1200: { perPage: 5 },
        1400: { perPage: 5 },
      },
      direction: document.documentElement.getAttribute("dir"),
    });

    main.on("move", (newIndex, prevIndex) => {
      const prevSlide = gallery.querySelector(`.splide__slide:nth-child(${prevIndex + 1}`);

      if (prevSlide) {
        if (prevSlide.querySelector("iframe")) {
          prevSlide.querySelector("iframe").src = prevSlide.querySelector("iframe").src;
        }
        if (prevSlide.querySelector("video")) {
          prevSlide.querySelector("video").pause();
        }
      }

      const currentSlide = gallery.querySelector(`.splide__slide:nth-child(${newIndex + 1}`);

      if (currentSlide) {
        if (currentSlide.querySelector("video")) {
          currentSlide.querySelector("video").play();
        }
      }
    });

    window.addEventListener(
      "variantChange.ecomify.product",
      (event) => {
        if (main.root.closest(".product-gallery-variant-based")) return;

        const selectedVariant = event.detail;

        if (selectedVariant.featured_media && totalSlides > 1) {
          main.go(selectedVariant.featured_media.position - 1);
        }
      },
      false
    );

    if (totalSlides > 1) {
      main.sync(thumbs);
      main.mount();
      thumbs.mount();
    } else {
      main.mount();
      gallery.querySelector(".thumbs-splide")?.remove();
    }

    if (window.matchMedia("(min-width: 992px)").matches) {
      const navbarHeight = document.querySelector("#navbar-desktop")?.clientHeight || 0;
      const announcementBarHeight = document.querySelector('[id*="__announcement-bar"].sticky-top')?.clientHeight || 0;

      const galleryWrapper = gallery.closest(".product-gallery-wrapper");
      if (!galleryWrapper) return;

      galleryWrapper.style.position = "sticky";
      galleryWrapper.style.top = `${navbarHeight + announcementBarHeight + 20}px`;
      galleryWrapper.style.zIndex = "1";
    }

    if (window.GLightbox) {
      // eslint-disable-next-line no-unused-vars
      const lightbox = GLightbox({
        selector: `[data-gallery="product-gallery-${gallery.dataset.productId}"]`,
        videosWidth: "1290px",
        loop: totalSlides > 1,
      });
      lightbox.on("open", () => {
        gallery.querySelectorAll("video").forEach((video) => {
          video.pause();
        });
      });
      lightbox.on("slide_changed", ({ prev, current }) => {
        if (totalSlides > 1) {
          main.go(current.index);
        }
      });
    }

    const modelViewer = gallery.querySelector("model-viewer");

    if (modelViewer) {
      const increaseBtn = gallery.querySelector("[data-model-3d-increase-zoom]");
      const decreaseBtn = gallery.querySelector("[data-model-3d-decrease-zoom]");
      const fullscreenBtn = gallery.querySelector("[data-model-3d-fullscreen]");

      increaseBtn.addEventListener("click", () => {
        modelViewer.zoom(1);
      });

      decreaseBtn.addEventListener("click", () => {
        modelViewer.zoom(-1);
      });

      fullscreenBtn.addEventListener("click", () => {
        if (document.fullscreenElement) {
          document.exitFullscreen();
          fullscreenBtn.querySelector(".bi-fullscreen").removeAttribute("hidden");
          fullscreenBtn.querySelector(".bi-fullscreen-exit").setAttribute("hidden", "hidden");
        } else {
          modelViewer.closest(".product-gallery-model-3d").requestFullscreen();
          fullscreenBtn.querySelector(".bi-fullscreen").setAttribute("hidden", "hidden");
          fullscreenBtn.querySelector(".bi-fullscreen-exit").removeAttribute("hidden");
        }
      });
    }
  });
};
initProductGallery();

document.addEventListener("shopify:section:load", (e) => {
  if (e.target.querySelector(".product-gallery")) {
    initProductGallery();
  }
});

window.addEventListener("updated.ecomify.collection", initProductGallery);

/*
    Inventory Bar
*/
const initInventoryBar = () => {
  const wrapper = document.querySelector("#inventory-bar");

  if (!wrapper) return;

  const progressBar = wrapper.querySelector(".progress-bar");

  setTimeout(() => {
    progressBar.style.width = progressBar.dataset.width;
  }, 250);
};
initInventoryBar();

document.addEventListener("shopify:section:load", (e) => {
  if (e.target.querySelector("#inventory-bar")) {
    initInventoryBar();
  }
});

/*
    Upsell modal
*/
const initUpsellModal = () => {
  const modal = document.querySelector("#product-upsell-modal");
  if (!modal) return;

  const form = document.querySelector('#product-content form[onsubmit*="onSubmitAtcForm"]');
  if (!form) return;

  form.setAttribute("data-ecomify-upsell-modal", "true");

  form.addEventListener("submit", (e) => {
    // Call the original onSubmitAtcForm function first
    if (window.onSubmitAtcForm) {
      window.onSubmitAtcForm(form, e);
    }

    const modal = bootstrap.Modal.getOrCreateInstance("#product-upsell-modal");
    modal.show();
  });

  document.querySelectorAll("#product-upsell-modal .btn-atc").forEach((btn) => {
    btn.addEventListener("click", () => {
      const footerBtn = document.querySelector("#product-upsell-modal .modal-footer .btn");
      footerBtn.classList.remove(footerBtn.dataset.defaultClass);
      footerBtn.classList.add(footerBtn.dataset.activeClass);
    });
  });

  modal.addEventListener("hidden.bs.modal", () => {
    if (document.querySelector("#offcanvas-cart")) {
      const offcanvasCart = bootstrap.Offcanvas.getOrCreateInstance("#offcanvas-cart");
      offcanvasCart.show();
    }
  });
};
initUpsellModal();

document.addEventListener("shopify:section:load", (e) => {
  if (e.target.querySelector("#product-upsell-modal")) {
    initUpsellModal();
  }
});

/*
    Bought Together
*/
const initBoughtTogether = () => {
  if (!document.querySelector("#product-bought-together")) return;

  const setTotalPrice = () => {
    let totalCompareAtPrice = 0;
    let totalPrice = 0;

    document.querySelectorAll("#product-bought-together .product-item-price").forEach((elem) => {
      if (elem.closest(".product-item").querySelector(".bought-together-check:checked")) {
        totalCompareAtPrice += elem.dataset.compareAtPrice.length
          ? Number(elem.dataset.compareAtPrice)
          : Number(elem.dataset.price);
        totalPrice += Number(elem.dataset.price);
      }
    });

    const totalElem = document.querySelector("#bought-together-total");

    if (totalCompareAtPrice > totalPrice) {
      totalElem.innerHTML = `
                ${totalElem.dataset.textTotalPrice}: 
                <span class="bought-together-total-compare-price text-muted me-2">
                    <span class="visually-hidden">
                        ${window.theme.product.priceFrom} &nbsp;
                    </span>
                     
                        ${Shopify.formatMoney(totalCompareAtPrice)}
                    
                </span>
                <span class="bought-together-total-price text-success">
                    <span class="visually-hidden">
                        ${window.theme.product.priceSale} &nbsp;
                    </span>
                    ${Shopify.formatMoney(totalPrice)}
                </span>
            `;
    } else {
      totalElem.innerHTML = `
                ${totalElem.dataset.textTotalPrice}: 
                <span class="bought-together-total-price">
                    ${Shopify.formatMoney(totalPrice)}
                </span>
            `;
    }
  };

  document.querySelectorAll(".bought-together-check").forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) {
        checkbox.closest(".product-item").style.opacity = "1";
      } else {
        checkbox.closest(".product-item").style.opacity = ".5";
      }

      if (document.querySelector(".bought-together-check:checked")) {
        document.querySelector("#bought-together-atc").disabled = false;
      } else {
        document.querySelector("#bought-together-atc").disabled = true;
      }

      setTotalPrice();
    });
  });

  document.querySelectorAll('#product-bought-together select[name="id"]').forEach((select) => {
    select.addEventListener("change", () => {
      const compareAtPrice = select.options[select.selectedIndex].dataset.compareAtPrice;
      const price = select.options[select.selectedIndex].dataset.price;

      select.closest(".product-item").querySelector(".product-item-price").setAttribute("data-compare-at-price", compareAtPrice);
      select.closest(".product-item").querySelector(".product-item-price").setAttribute("data-price", price);

      setTotalPrice();
    });
  });

  document.querySelector("#bought-together-atc").addEventListener("click", async (e) => {
    const btn = e.target;

    btn.style.width = btn.clientWidth + 4 + "px";
    btn.innerHTML = `
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        `;

    const items = [];

    document.querySelectorAll("#product-bought-together .product-item").forEach((elem) => {
      if (elem.querySelector(".bought-together-check:checked")) {
        items.push({
          id: Number(elem.querySelector('[name="id"]').value),
          quantity: 1,
        });
      }
    });

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });

    btn.innerHTML = "Added to Cart";

    setTimeout(() => {
      btn.innerHTML = btn.dataset.textAddToCart;
    }, 2000);

    window.refreshCartContents(response);

    bootstrap.Offcanvas.getOrCreateInstance("#offcanvas-cart").show();
  });
};
initBoughtTogether();

/*
    Qty Breaks
*/
const initQtyBreaks = () => {
  document.querySelectorAll('[name="product-qty-breaks"]').forEach((radio) => {
    radio.addEventListener("change", () => {
      const qty = Number(radio.dataset.qty);

      const qtyField = radio.closest("form").querySelector('[name="quantity"]');

      const form = radio.closest("form");
      const btn = form.querySelector('button[name="add"]');

      if (!qtyField) return;

      qtyField.value = qty;
      qtyField.dispatchEvent(new Event("change"));

      setTimeout(() => {
        btn.classList.add("animate__animated", "animate__shakeX");
      }, 250);

      setTimeout(() => {
        btn.classList.remove("animate__animated", "animate__shakeX");
      }, 1500);

      if (form.querySelector(".btn-atc-price")) {
        form.querySelector(".btn-atc-price").textContent = radio.dataset.totalPrice;
      }

      document.querySelectorAll('[name="product-qty-breaks"]').forEach((radio) => {
        radio.closest(".form-check").classList.remove("form-check-is-checked");
      });

      radio.closest(".form-check").classList.add("form-check-is-checked");
    });
  });

  document.querySelectorAll('[name="product-qty-breaks"]').forEach((radio) => {
    radio
      .closest(".form-check")
      .querySelector("label")
      .addEventListener("click", () => {
        if (radio.closest(".form-check").classList.contains("form-check-is-checked")) {
          radio.closest(".form-check").classList.remove("form-check-is-checked");

          setTimeout(() => {
            document.querySelectorAll('[name="product-qty-breaks"]').forEach((radio) => {
              radio.checked = false;
            });

            const qtyField = radio.closest("form").querySelector('[name="quantity"]');
            qtyField.value = "1";

            const form = radio.closest("form");
            const qtyBreaksList = radio.closest("#product-qty-breaks-list");

            if (form.querySelector(".btn-atc-price")) {
              form.querySelector(".btn-atc-price").textContent = qtyBreaksList.dataset.currentVariantPrice;
            }
          }, 100);
        }
      });
  });
};
initQtyBreaks();

/*
    Prev/Next Products (will show only if the product is within a collection)
*/
const initPrevNextButtons = async () => {
  const wrapper = document.querySelector("#product-prev-next");

  if (!wrapper) return;

  if (!window.matchMedia("(max-width: 1599px)").matches) return;

  await new Promise((resolve) => setTimeout(resolve, 1000));

  const threshold =
    document.querySelector('.product-content form[action*="/cart/add"]').getBoundingClientRect().bottom + window.scrollY;

  window.addEventListener("scroll", () => {
    if (window.scrollY > threshold) {
      wrapper.classList.add("show");
    } else {
      wrapper.classList.remove("show");
    }
  });
};
initPrevNextButtons();

/*
    Personalization (Custom properties)
*/
const initPersonalization = () => {
  document.querySelectorAll('.product-personalization-field[data-type="checkbox"]').forEach((elem) => {
    let values = [];

    elem.querySelectorAll("input").forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        if (checkbox.checked) {
          values.push(checkbox.value);
        } else {
          values = values.filter((item) => item !== checkbox.value);
        }

        elem.querySelector('[type="hidden"]').value = values.join(", ");
      });
    });
  });
};
initPersonalization();

/*
    Limited Offer
*/
const initLimitedOffer = () => {
  const block = document.querySelector("#product-limited-offer");

  if (!block) return;

  const icon = block.querySelector(".block-icon");

  setInterval(() => {
    icon.classList.add("animate__animated", "animate__tada");

    setTimeout(() => {
      icon.classList.remove("animate__animated", "animate__tada");
    }, 1500);
  }, 3000);
};
initLimitedOffer();

/*
    Sync quantity between the product form, Sticky ATC, etc
*/
const initSyncQuantity = () => {
  const productFormQtyInput = document.querySelector('.product-buy-buttons input[name="quantity"]');

  if (!productFormQtyInput) return;

  productFormQtyInput.addEventListener("change", () => {
    const stickyAtcQtyInput = document.querySelector('#sticky-atc input[name="quantity"]');

    if (!stickyAtcQtyInput) return;

    stickyAtcQtyInput.value = productFormQtyInput.value;
  });
};
initSyncQuantity();

/*
 product-progress-block
*/

document.addEventListener("DOMContentLoaded", function () {
  const productProgressCircles = document.querySelectorAll(".product-progress-circle");
  productProgressCircles.forEach((circle) => {
    const targetPercentage = parseInt(circle.dataset.percentage);
    const valueDisplay = circle.querySelector(".progress-value");
    let currentPercentage = 0;

    const animateProgress = () => {
      if (currentPercentage < targetPercentage) {
        currentPercentage++;
        circle.style.setProperty("--percentage", currentPercentage + "%");
        valueDisplay.textContent = currentPercentage + "%";
        requestAnimationFrame(animateProgress);
      }
    };

    setTimeout(animateProgress, 100);
  });
});

/*
    Label Preview System
*/

// Utility functions for preview
const LabelPreview = {
  // Get current selected color
  getCurrentColor() {
    // Get the selected color input directly (same principle as font)
    const selectedColorInput = document.querySelector('.color-swatches input[type="radio"]:checked');
    if (!selectedColorInput) return "black";

    let colorName = selectedColorInput.value;

    if (colorName.toLowerCase().includes("gold")) {
      return "gold";
    } else if (colorName.toLowerCase().includes("silver")) {
      return "silver";
    }
    return "black";
  },

  // Get current selected font
  getCurrentFont() {
    const fontSelect = document.querySelector(".product-option.form-select");
    if (fontSelect && fontSelect.value) {
      return fontSelect.value;
    }
    return "Block";
  },

  // Apply text filtering based on font and configuration
  filterText(text, selectedFont, config) {
    if (!config) return text;

    // Global restrictions first
    if (config.numbersSymbolsAndSpecialCharactersAllowed) {
      return text.replace(/[^a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g, "");
    } else if (config.numberAndSymbolsAllowed) {
      return text.replace(/[^a-zA-Z0-9]/g, "");
    } else if (config.numbersOnly) {
      return text.replace(/[^0-9]/g, "");
    }

    // Font-specific restrictions
    if ((selectedFont === "Block" && !config.numbersSymbolsAndSpecialCharactersAllowed) || selectedFont === "Gothic") {
      return text.replace(/[^a-zA-Z0-9 ]/g, "");
    } else if (selectedFont === "Script") {
      return text.replace(/[0-9]/g, "");
    }

    return text;
  },

  // Update the preview display
  updatePreview(inputValue) {
    const labelPreview = document.getElementById("label-preview");
    if (!labelPreview) return;

    const currentFont = this.getCurrentFont();
    const currentColor = this.getCurrentColor();

    // Update content and styling
    labelPreview.textContent = inputValue || "";
    labelPreview.style.fontFamily = currentFont;
    labelPreview.style.color = currentColor;

    // Toggle visibility
    if (inputValue && inputValue.trim().length > 0) {
      labelPreview.classList.remove("close");
      labelPreview.classList.add("open");
    } else {
      labelPreview.classList.add("close");
      labelPreview.classList.remove("open");
    }
  },

  // Initialize the preview system
  init() {
    // Initialize preview on page load
    this.updatePreview("");

    // Handle text input changes
    document.querySelectorAll(".label-preview").forEach((inputField) => {
      if (inputField.tagName.toLowerCase() === "input") {
        inputField.addEventListener("input", (e) => {
          const config = window.config || {};
          const selectedFont = this.getCurrentFont();

          // Filter the input
          const filteredValue = this.filterText(e.target.value, selectedFont, config);
          if (e.target.value !== filteredValue) {
            e.target.value = filteredValue;
          }

          // Update preview
          this.updatePreview(filteredValue);
        });
      }
    });

    // Handle font changes
    document.querySelectorAll(".product-option.form-select").forEach((select) => {
      select.addEventListener("change", () => {
        const inputField = document.querySelector(".label-preview[type]");
        if (inputField) {
          this.updatePreview(inputField.value);
        }
      });
    });

    // Handle color swatch changes - direct event listeners
    document.querySelectorAll('.color-swatches input[type="radio"]').forEach((colorInput) => {
      colorInput.addEventListener("change", () => {
        const inputField = document.querySelector(".label-preview[type]");
        if (inputField) {
          this.updatePreview(inputField.value);
        }
      });
    });

    // Handle color changes (this will be called from the existing onChangeProductOption function)
    window.updateLabelPreview = () => {
      const inputField = document.querySelector(".label-preview[type]");
      if (inputField) {
        this.updatePreview(inputField.value);
      }
    };
  },
};

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  LabelPreview.init();
});
